import { t } from "../i18n/catalogue.js";
/**
 * The shapes an income funnel is drawn from, and the arithmetic that narrows it.
 *
 * ==========================================================================================
 * `dashboard.md` REFUSES A FUNNEL, AND THIS IS NOT THE ONE IT REFUSES
 * ==========================================================================================
 *
 * That refusal is specific and it still stands:
 *
 *   "A funnel for In → Out → Left. No. A funnel draws one population narrowing through ORDERED STAGES,
 *   and this has one stage — `Out` is not a subset of `In`, it is an independent quantity that can
 *   exceed it."
 *
 * It is right about a three-band In/Out/Left funnel: `Out` is not a stage of `In`, so drawing it as one
 * asserts a relationship that is not there.
 *
 * THIS FUNNEL HAS REAL STAGES. Income at the top, then each expense category taken out in turn, then what
 * remains. Every band is the money STILL UNSPENT after that category — a genuinely narrowing population,
 * each stage a subset of the one above it, which is exactly the shape a funnel is for. And unlike the
 * refused version the arithmetic closes: the categories partition `Out`, and `In − Out` is the last band.
 *
 * ==========================================================================================
 * CLOSURE IS THE CONSTRAINT, AND IT NEEDS NO NEW DATA
 * ==========================================================================================
 *
 * A funnel asserts that its stages account for the whole. The expense ranking deliberately does not — it
 * is a top ten of up to seventeen with no "Other" bucket, because a ranking claims no whole and FR-067
 * forbids a part-of-whole mark beside its rows.
 *
 * So a funnel drawn from the shown categories alone would not close, and the gap would be silent. It does
 * not need a wider payload to fix that: `totalCents` is documented as the whole of `Out` including the
 * categories that are NOT shown, so the unshown ones are exactly `totalCents − Σ(shown)` and get one band
 * of their own. That band is what makes the funnel honest, and it is why this module refuses to build one
 * without a total to reconcile against.
 *
 * ==========================================================================================
 * AND WHY THE NEXT PERSON SHOULD NOT ADD AN "OTHER" BUCKET TO THE PAYLOAD
 * ==========================================================================================
 *
 * It is the obvious tidier move and it would cost the page its best property here. `totalCents` IS the
 * health card's `Out` — one figure, grouped from one reconstruction (AC-D11) — so a funnel that takes its
 * total from there has a closing band that cannot disagree with the `Out` card beside it. Not "agrees
 * with": IS.
 *
 * A remainder computed server-side and shipped as its own field would be a SECOND computation of a figure
 * already on screen, which is the defect this page keeps producing: the summary card recomputing `Out`
 * until `dashboard-scale.test.ts` made it visible, the ranking recomputing it household-only while the
 * card was per-member, three single-date pillar reads recomputing net worth. Every one of them agreed
 * until it didn't. Deriving the remainder by subtraction keeps there being one number.
 */

/** One category taken out of the flow. Already formatted figures stay with the caller. */
export interface FunnelStage {
  key: string;
  label: string;
  /** A POSITIVE magnitude — what this stage removes. */
  cents: bigint;
}

export interface FunnelInput {
  /** What came in. The width of the top band, and the reference everything else is a share of. */
  inCents: bigint;
  /**
   * The whole of what went out, INCLUDING categories not present in `stages`.
   *
   * Required rather than derived, because deriving it from the stages is what would let a top-ten pass as
   * a partition. The difference becomes its own band.
   */
  outCents: bigint;
  /** The categories to draw, largest first. Need not be exhaustive; the remainder is drawn for you. */
  stages: readonly FunnelStage[];
}

/** One drawn band: what is still unspent at this point, and what the step above it removed. */
export interface FunnelBand {
  key: string;
  label: string;
  /**
   * The band's width. For every band but the last one of a deficit month, the money still unspent AFTER
   * this band's step.
   *
   * NEVER NEGATIVE, because a trapezoid has no negative width. A household that overspent narrows to
   * nothing and then gets an `over` band whose width is the SHORTFALL — see `over` below, and the note on
   * `funnelBands` about why that band is allowed to be wider than the one before it when nothing else is.
   */
  remainingCents: bigint;
  /** What this step took out. Zero for the top band and for the closing bands, which take nothing. */
  removedCents: bigint;
  /**
   * THE ONE BAND THAT IS NOT REMAINING MONEY. It is how far past zero the spending went.
   *
   * Flagged rather than inferred from the key, because the drawing has to treat it differently — its own
   * fill, and exempt from the "never wider than the band above" rule that every other band obeys.
   */
  over?: boolean;
}

export interface FunnelShape {
  bands: FunnelBand[];
  /**
   * How far past income the spending went, or zero. POSITIVE when overspent.
   *
   * The one figure the drawing cannot carry, which is why it is returned separately rather than folded
   * into a band nobody can see.
   */
  overspentCents: bigint;
}

/**
 * Turn income and its categories into narrowing bands.
 *
 * The top band is income itself. Each stage removes its own amount and the band that follows is what
 * remains. An "Other categories" band appears when the stages do not account for all of `outCents`, and a
 * final band shows what is left.
 */
export function funnelBands(input: FunnelInput): FunnelShape {
  const bands: FunnelBand[] = [
    { key: "in", label: "In", remainingCents: input.inCents, removedCents: 0n },
  ];

  let remaining = input.inCents;
  let accountedFor = 0n;

  const take = (key: string, label: string, amount: bigint): void => {
    if (amount <= 0n) return;
    accountedFor += amount;
    /*
     * FLOORED AT ZERO. Once the money has run out the bands stop narrowing — a negative width cannot be
     * drawn, and a band that grew again would read as money coming back. What actually happened is
     * reported by `overspentCents`, in words, beside the chart.
     */
    remaining = remaining - amount > 0n ? remaining - amount : 0n;
    bands.push({ key, label, remainingCents: remaining, removedCents: amount });
  };

  for (const stage of input.stages) take(stage.key, stage.label, stage.cents);

  /*
   * THE UNSHOWN CATEGORIES, as one band. This is what makes the funnel close, and it exists because the
   * ranking beside it is deliberately a top ten rather than a partition. Absent when the stages already
   * account for everything, so a household using fewer categories than the limit sees no phantom band.
   */
  take("other", t("funnel.otherCategories"), input.outCents - accountedFor);

  const overspent = overspendOf(input);

  /*
   * THE CLOSING BAND, AND THERE ARE TWO KINDS OF IT.
   *
   * A surplus month ends with what is left. A deficit month has nothing left, so `Left` would be a
   * zero-width band saying nothing — and the figure that matters is how far past nothing it went. So the
   * deficit gets an `over` band instead, whose width IS the shortfall.
   *
   * THAT BAND IS WIDER THAN THE ONE ABOVE IT, and it is the only band allowed to be. Every other band is
   * remaining money, so one growing would read as money coming back — which is why `funnel-chart.test.tsx`
   * asserts monotonic narrowing and exempts exactly this band. It is drawn in its own fill and labelled
   * "Over by …", so it reads as the funnel having gone past its own end rather than as the flow widening.
   */
  if (overspent > 0n) {
    bands.push({
      key: "over",
      label: t("funnel.overBy"),
      remainingCents: overspent,
      removedCents: 0n,
      over: true,
    });
  } else {
    bands.push({
      key: "left",
      label: t("funnel.left"),
      remainingCents: remaining,
      removedCents: 0n,
    });
  }

  return { bands, overspentCents: overspent };
}

/** Nothing to draw: no income to be a proportion of, and nothing spent either. */
export function isEmptyFunnel(input: FunnelInput): boolean {
  return input.inCents <= 0n && input.outCents <= 0n;
}

/**
 * How far past income the spending went, or zero.
 *
 * Exported so the section stating it in words and the shape flooring its bands at zero read ONE definition.
 * `funnelBands` returns the same figure for a caller that already has the bands; this is for the consumer
 * that only wants the sentence.
 */
export function overspendOf(input: Pick<FunnelInput, "inCents" | "outCents">): bigint {
  return input.outCents > input.inCents ? input.outCents - input.inCents : 0n;
}
